import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';
import { formatApiError } from '@/lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_inventory-engine-2/artifacts/2d9zezs2_Zippy%20Logo%202.PNG';

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [needsSetup, setNeedsSetup] = useState(false);

  // Poll setup-status on mount so we can surface a manual "First time here?"
  // fallback if the auto-redirect from SetupGate missed for any reason
  // (network hiccup, cached bundle, cross-domain deploy, etc.). A short delay
  // avoids a flash of the banner before SetupGate's redirect wins on happy paths.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      axios.get(`${API}/auth/setup-status`).then((res) => {
        if (!cancelled && res.data?.needs_setup) setNeedsSetup(true);
      }).catch(() => { /* silent: not fatal */ });
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      toast.success(`Welcome back, ${response.data.user.name}`);
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      const msg = formatApiError(error);
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1672552226669-f6c3041972ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwc2hlbGYlMjBwZXJzcGVjdGl2ZSUyMGNhcmRib2FyZCUyMHNoaXBwaW5nJTIwYm94JTIwb3BlbiUyMGJhcmNvZGUlMjBzY2FubmVyJTIwaGFuZGhlbGQlMjBkZWxpdmVyeSUyMHRydWNrJTIwbG9naXN0aWNzfGVufDB8fHx8MTc3Mzg3NjAyMXww&ixlib=rb-4.1.0&q=85"
          alt="Warehouse"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1680e5]/90 to-[#00a89c]/90 backdrop-blur-[2px]" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white">
            <h1 className="text-5xl font-bold mb-4">Zippy Warehouse Management</h1>
            <p className="text-xl text-white/90">
              Streamline your inventory, picking, and packing operations
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <img src={LOGO_URL} alt="Zippy WMS" className="h-16 w-auto" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Welcome back</CardTitle>
              <CardDescription>Sign in to your Zippy WMS account</CardDescription>
            </CardHeader>
            <CardContent>
              {needsSetup && (
                <div
                  className="mb-4 flex items-start gap-3 rounded-lg border border-[#00a89c]/40 bg-[#00a89c]/5 p-4"
                  data-testid="login-needs-setup-banner"
                >
                  <Sparkles className="h-5 w-5 text-[#00a89c] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900">First time here?</p>
                    <p className="text-xs text-slate-600 mb-2">
                      This Zippy WMS instance has no accounts yet. Create your Super Admin to get started.
                    </p>
                    <Button asChild size="sm" className="bg-[#00a89c] hover:bg-[#00a89c]/90">
                      <Link to="/setup" data-testid="login-go-to-setup">
                        Set up your account
                      </Link>
                    </Button>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-[#1680e5] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    data-testid="login-password-input"
                  />
                </div>
                {errorMsg && (
                  <div
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                    data-testid="login-error"
                  >
                    {errorMsg}
                  </div>
                )}
                <Button
                  type="submit"
                  className="w-full bg-[#1680e5] hover:bg-[#1680e5]/90"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>
              <div className="mt-4 text-center text-xs text-slate-500">
                Trouble signing in? Contact your Super Admin.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
